import type SettingsController from '../controllers/settings/settings.controller'
import type { AppearanceSettings, SettingsPayload, SettingsRecord } from '../models/settings.model'
import type { SettingsWriteOptions } from '../repositories/settings/settings.repository'

const APPEARANCE_SCOPE = 'appearance'

export function isValidTimezone(value: unknown): value is string {
  if (typeof value !== 'string') return false

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

// поля, которые пришли с бэка битыми или отсутствуют, берём из fallback
function mergeAppearance(fallback: AppearanceSettings, payload: SettingsPayload): AppearanceSettings {
  return {
    timezone: isValidTimezone(payload.timezone) ? payload.timezone : fallback.timezone,
    currency: typeof payload.currency === 'string' ? payload.currency : fallback.currency,
    soundVolume: typeof payload.soundVolume === 'number' ? payload.soundVolume : fallback.soundVolume
  }
}

export default class SettingsService {
  constructor(private controller: SettingsController) {
  }

  getAll(): Promise<SettingsRecord[]> {
    return this.controller.getAll()
  }

  getScope<T extends SettingsPayload = SettingsPayload>(
    scope: string
  ): Promise<SettingsRecord<T> | null> {
    return this.controller.getScope<T>(scope)
  }

  putScope<T extends SettingsPayload = SettingsPayload>(
    scope: string,
    payload: T,
    options?: SettingsWriteOptions
  ): Promise<SettingsRecord<T>> {
    return this.controller.putScope<T>(scope, payload, options)
  }

  patchScope<T extends SettingsPayload = SettingsPayload>(
    scope: string,
    payload: Partial<T>
  ): Promise<SettingsRecord<T>> {
    return this.controller.patchScope<T>(scope, payload)
  }

  deleteScope(scope: string): Promise<void> {
    return this.controller.deleteScope(scope)
  }

  // null — записи ещё нет: пользователь пришёл с версии, где настройки жили только в localStorage.
  // Выгружать ли локальный набор, решает вызывающий: к этому моменту он мог устареть
  async loadAppearance(local: AppearanceSettings): Promise<AppearanceSettings | null> {
    const record = await this.controller.getScope(APPEARANCE_SCOPE)
    return record ? mergeAppearance(local, record.payload) : null
  }

  // Всегда полный набор: PUT не зависит от того, мержит бэк PATCH или заменяет payload
  async saveAppearance(settings: AppearanceSettings): Promise<void> {
    await this.controller.putScope(APPEARANCE_SCOPE, settings)
  }
}
